/**
 * Embed utilities for 3rd-party media platforms (video and audio)
 */

import { AssetsDrmService } from '../services/assets-drm.service'
import { AUDIO_EMBED_PLATFORMS, EmbedSource, isVimeoEmbedInfo, VIDEO_EMBED_PLATFORMS } from '../types'
import { computeAspectRatio } from './dimensions.utils'
import type { EmbedInfo, EmbedPlatform, OEmbedFields, VimeoEmbedInfo } from '../types'

export type { EmbedPlatform, EmbedInfo, OEmbedFields, VimeoEmbedInfo } from '../types'

/** Presence of an iframe tag, case-insensitive and boundary-aware so `<iframexyz` does not match. */
const IFRAME_TAG_PATTERN = /<iframe\b/i

/** First `src` attribute of an `<iframe>` in an HTML string, single or double quoted. */
const IFRAME_SRC_PATTERN = /<iframe[^>]*\ssrc\s*=\s*["']([^"']+)["']/i

/** Entities an oEmbed provider escapes inside the iframe `src` attribute. */
const HTML_ENTITIES: ReadonlyArray<[RegExp, string]> = [
  [/&amp;/gi, '&'],
  [/&lt;/gi, '<'],
  [/&gt;/gi, '>'],
  [/&quot;/gi, '"'],
  [/&#0*39;/gi, "'"],
]

function decodeHtmlEntities(value: string): string {
  return HTML_ENTITIES.reduce((acc, [pattern, char]) => acc.replace(pattern, char), value)
}

/**
 * Extract the embed URL from an iframe HTML string, or return the input when it
 * is already a URL.
 *
 * Regex rather than `DOMParser`: this runs during Nuxt SSR, where `DOMParser`
 * is undefined. Extraction used to throw there and fall back to the raw HTML,
 * which the platform patterns still matched by substring — so YouTube/Vimeo/
 * Loom/Wistia/Spotify looked fine while the SoundCloud and Mixcloud branches,
 * which reuse the extracted string as the embed URL, leaked the whole
 * `<iframe …>` tag into the player URL (SoundCloud) or threw in `new URL()` and
 * returned no embed URL at all (Mixcloud).
 *
 * Regex on both server and client also keeps the two renders in agreement.
 * Note this reads the raw attribute, whereas `iframe.src` resolved relative
 * URLs against the document; embed providers always emit absolute URLs, and a
 * relative one matches no platform pattern either way.
 */
export function extractUrlFromIframe(htmlOrUrl: string): string {
  if (!IFRAME_TAG_PATTERN.test(htmlOrUrl)) return htmlOrUrl

  const src = htmlOrUrl.match(IFRAME_SRC_PATTERN)?.[1]
  return src ? decodeHtmlEntities(src) : htmlOrUrl
}

/** Vimeo privacy hashes are opaque URL-safe alphanumeric values (not hex-only). */
const VIMEO_PRIVACY_HASH_PATTERN = '[A-Za-z0-9_-]+'

/** Vimeo page/player URLs and iframe src — privacy hash only when present in the pasted URL. */
function buildVimeoEmbedInfo(extractedUrl: string): VimeoEmbedInfo | null {
  const vimeoPattern = new RegExp(`(?:player\\.)?vimeo\\.com/(?:video/)?(\\d+)(?:/(${VIMEO_PRIVACY_HASH_PATTERN}))?`, 'i')
  const vimeoMatch = extractedUrl.match(vimeoPattern)
  if (!vimeoMatch) return null

  const videoId = vimeoMatch[1]
  const privacyHash = vimeoMatch[2] || extractedUrl.match(new RegExp(`[?&]h=(${VIMEO_PRIVACY_HASH_PATTERN})`, 'i'))?.[1]
  return {
    platform: EmbedSource.VIMEO,
    contentId: videoId,
    ...(privacyHash ? { privacyHash } : {}),
    embedUrl: privacyHash
      ? `https://player.vimeo.com/video/${videoId}?h=${privacyHash}`
      : `https://player.vimeo.com/video/${videoId}?autoplay=1`,
    // Vimeo thumbnails require API call, we'll use a placeholder or fetch it separately
    thumbnailUrl: undefined,
  }
}

/**
 * Detect embed platform and generate embed URL
 * Supports: YouTube, Vimeo, Loom, Wistia, Spotify, SoundCloud, Mixcloud
 * Accepts both direct URLs and iframe HTML strings
 */
export function getEmbedInfo(url: string): EmbedInfo {
  // Extract URL from iframe HTML if needed
  const extractedUrl = extractUrlFromIframe(url)

  // YouTube
  const youtubePattern = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|live|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i
  const youtubeMatch = extractedUrl.match(youtubePattern)
  if (youtubeMatch) {
    const videoId = youtubeMatch[1]
    return {
      platform: EmbedSource.YOUTUBE,
      contentId: videoId,
      embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1`,
      thumbnailUrl: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
    }
  }

  // Vimeo — hash (`?h=` / `/id/hash`) only for unlisted/private links; public URLs keep the legacy player URL.
  const vimeoInfo = buildVimeoEmbedInfo(extractedUrl)
  if (vimeoInfo) return vimeoInfo

  // Loom - matches both /share/ and /embed/ URLs
  const loomPattern = /loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/i
  const loomMatch = extractedUrl.match(loomPattern)
  if (loomMatch) {
    const videoId = loomMatch[1]
    return {
      platform: EmbedSource.LOOM,
      contentId: videoId,
      embedUrl: `https://www.loom.com/embed/${videoId}?autoplay=1`,
      // Loom thumbnail filenames carry a hash only the oEmbed payload knows
      // (`<id>-<hash>.gif`), so any URL-derived guess 403s. Same as Vimeo above:
      // leave it to the caller's oEmbed lookup.
      thumbnailUrl: undefined,
    }
  }

  // Wistia - matches /medias/ and /embed/ URLs
  const wistiaPattern = /wistia\.(?:com|net)\/(?:medias|embed\/iframe)\/([a-zA-Z0-9]+)/i
  const wistiaMatch = extractedUrl.match(wistiaPattern)
  if (wistiaMatch) {
    const videoId = wistiaMatch[1]
    return {
      platform: EmbedSource.WISTIA,
      contentId: videoId,
      embedUrl: `https://fast.wistia.net/embed/iframe/${videoId}`,
    }
  }

  // Spotify - matches /embed/ and /track|episode|playlist|album/ URLs
  const spotifyPattern = /open\.spotify\.com\/(?:embed\/)?(track|episode|playlist|album)\/([a-zA-Z0-9]+)/i
  const spotifyMatch = extractedUrl.match(spotifyPattern)
  if (spotifyMatch) {
    const contentType = spotifyMatch[1]
    const contentId = spotifyMatch[2]
    return {
      platform: EmbedSource.SPOTIFY,
      contentId,
      embedUrl: `https://open.spotify.com/embed/${contentType}/${contentId}`,
    }
  }

  // SoundCloud - matches player embed URLs and regular URLs
  const soundcloudPattern = /soundcloud\.com/i
  if (soundcloudPattern.test(extractedUrl)) {
    // Already a player embed URL - use as-is
    if (extractedUrl.includes('w.soundcloud.com/player')) {
      return { platform: EmbedSource.SOUNDCLOUD, embedUrl: extractedUrl }
    }
    // Regular page URL - convert to embeddable player URL
    return {
      platform: EmbedSource.SOUNDCLOUD,
      embedUrl: `https://w.soundcloud.com/player/?url=${encodeURIComponent(extractedUrl)}&auto_play=false&visual=true`,
    }
  }

  // Mixcloud - matches player embed URLs and regular URLs
  const mixcloudPattern = /mixcloud\.com/i
  if (mixcloudPattern.test(extractedUrl)) {
    // Already a player/widget embed URL - use as-is
    if (extractedUrl.includes('player.mixcloud.com') || extractedUrl.includes('/widget/iframe')) {
      return { platform: EmbedSource.MIXCLOUD, embedUrl: extractedUrl }
    }
    // Regular page URL (e.g. https://www.mixcloud.com/artist/show/) - convert to embeddable player URL
    try {
      const url = new URL(extractedUrl)
      return {
        platform: EmbedSource.MIXCLOUD,
        embedUrl: `https://player.mixcloud.com/widget/iframe/?feed=${encodeURIComponent(url.pathname)}`,
      }
    } catch {
      return { platform: EmbedSource.MIXCLOUD, embedUrl: undefined }
    }
  }

  return { platform: 'custom' }
}

/**
 * Check if URL is a 3rd-party embed platform
 */
export function isThirdPartyEmbed(url: string): boolean {
  const info = getEmbedInfo(url)
  return info.platform !== 'custom'
}

/**
 * Check if URL is a 3rd-party audio embed (Spotify, SoundCloud, Mixcloud)
 */
export function isAudioEmbed(url: string): boolean {
  const info = getEmbedInfo(url)
  return AUDIO_EMBED_PLATFORMS.has(info.platform)
}

/**
 * Check if URL is a 3rd-party video embed (YouTube, Vimeo, Loom, Wistia)
 */
export function isVideoEmbed(url: string): boolean {
  const info = getEmbedInfo(url)
  return VIDEO_EMBED_PLATFORMS.has(info.platform)
}

/**
 * Get thumbnail URL for an embed (3rd party or custom)
 */
export function getEmbedThumbnail(url: string, fallbackPoster?: string): string | undefined {
  const info = getEmbedInfo(url)
  return info.thumbnailUrl || fallbackPoster
}

// ================= oEmbed Shared Helpers =================

const OEMBED_FIELD_KEYS: readonly (keyof OEmbedFields)[] = [
  'html',
  'thumbnail_url',
  'thumbnail_width',
  'thumbnail_height',
  'provider_name',
  'provider_url',
  'author_name',
  'author_url',
  'title',
  'width',
  'height',
] as const

/**
 * Apply raw oEmbed JSON onto any target object that extends OEmbedFields.
 * Mutates `target` in place and returns it for chaining.
 * If `updateName` is true and the oEmbed payload contains a title, `target.name` is also set.
 */
export function applyOEmbedMeta<T extends OEmbedFields & { name?: string }>(
  target: T,
  meta: Record<string, unknown> | null,
  updateName = true
): T {
  if (!meta) return target
  for (const key of OEMBED_FIELD_KEYS) {
    if (meta[key] !== undefined) {
      ;(target as Record<string, unknown>)[key] = meta[key]
    }
  }
  if (updateName && meta.title) {
    target.name = meta.title as string
  }
  // Derive aspectRatio from the width/height that oEmbed just returned so
  // callers don't have to re-run dimension helpers after this runs.
  const aspectRatio = computeAspectRatio(target.width, target.height)
  if (aspectRatio) {
    target.aspectRatio = aspectRatio
  }
  return target
}

/**
 * Pick only the oEmbed fields from a source object.
 * Use when spreading oEmbed data into an attachment payload.
 */
export function pickOEmbedFields(source: OEmbedFields): OEmbedFields {
  const result: OEmbedFields = {}
  for (const key of OEMBED_FIELD_KEYS) {
    if (source[key] !== undefined) {
      ;(result as Record<string, unknown>)[key] = source[key]
    }
  }
  return result
}

// ================= oEmbed Metadata Fetching (client-side only) =================

/**
 * oEmbed endpoint map keyed by EmbedSource.
 * Only platforms that expose a public, CORS-friendly oEmbed JSON endpoint.
 */
const OEMBED_ENDPOINTS: Partial<Record<EmbedSource, (url: string) => string>> = {
  [EmbedSource.YOUTUBE]: url => `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(url)}`,
  [EmbedSource.VIMEO]: url => `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`,
  [EmbedSource.LOOM]: url => `https://www.loom.com/v1/oembed?url=${encodeURIComponent(url)}`,
}

/**
 * Fetch full oEmbed metadata from the platform's public endpoint.
 * Returns the raw JSON response (html, thumbnail_url, provider_name, etc.)
 * or null when the platform is unsupported, the call fails, or we're on SSR.
 */
export async function fetchOEmbedMeta(urlString: string): Promise<Record<string, unknown> | null> {
  if (typeof window === 'undefined') return null

  const info = getEmbedInfo(urlString)
  const buildEndpoint = OEMBED_ENDPOINTS[info.platform as EmbedSource]
  if (!buildEndpoint) return null

  const oembedPageUrl =
    isVimeoEmbedInfo(info) && info.contentId
      ? `https://vimeo.com/${info.contentId}${info.privacyHash ? `/${info.privacyHash}` : ''}`
      : urlString

  try {
    const res = await fetch(buildEndpoint(oembedPageUrl))
    if (!res.ok) return null
    return await res.json()
  } catch (error) {
    console.warn('[fetchOEmbedMeta] Failed to fetch oEmbed metadata:', { url: urlString, platform: info.platform, error })
    return null
  }
}

// ================= Vimeo Thumbnails (client-side only) =================
const vimeoThumbCache = new Map<string, string>()

/**
 * Fetch Vimeo thumbnail using public oEmbed endpoint.
 * Returns undefined on SSR or failure. Results are cached per contentId.
 */
export async function fetchVimeoThumbnailById(videoId: string, privacyHash?: string): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined
  if (!videoId) return undefined
  const cacheKey = privacyHash ? `${videoId}:${privacyHash}` : videoId
  const cached = vimeoThumbCache.get(cacheKey)
  if (cached) return cached

  try {
    const vimeoPageUrl = privacyHash ? `https://vimeo.com/${videoId}/${privacyHash}` : `https://vimeo.com/${videoId}`
    const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoPageUrl)}`
    const res = await fetch(oembedUrl)
    if (!res.ok) return undefined
    const data = await res.json()
    const thumb: string | undefined = data?.thumbnail_url
    if (thumb) vimeoThumbCache.set(cacheKey, thumb)
    return thumb
  } catch {
    return undefined
  }
}

/**
 * Resolve a thumbnail URL for the given embed URL (supports Vimeo, DRM, YouTube, Loom).
 * @param url - Embed URL
 * @param fallbackPoster - Fallback poster image URL
 * @param licenseId - Optional DRM license ID for assets-drm videos
 */
export async function resolveEmbedThumbnail(url: string, fallbackPoster?: string, licenseId?: string): Promise<string | undefined> {
  // Check for DRM asset thumbnail first
  if (licenseId) {
    try {
      const service = new AssetsDrmService()
      const assetInfo = await service.getAssetInfo(licenseId)
      const drmThumb = assetInfo.thumbnailUrl || assetInfo.processedUrl || assetInfo.url
      if (drmThumb) return drmThumb
    } catch (err) {
      console.warn('Failed to fetch DRM asset thumbnail:', err)
      // Fall through to platform detection
    }
  }

  // Check for platform-specific thumbnails (YouTube, Loom)
  const info = getEmbedInfo(url)
  if (info.thumbnailUrl) return info.thumbnailUrl

  // Handle Vimeo async fetch
  if (isVimeoEmbedInfo(info) && info.contentId) {
    return (await fetchVimeoThumbnailById(info.contentId, info.privacyHash)) || fallbackPoster
  }

  return fallbackPoster
}
